import { useState } from "react";
import { useSWRConfig } from "swr";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiSWR } from "@/hooks/useApiSWR";
import { useDraftEditor } from "@/hooks/useDraftEditor";
import {
	approveInboundEmail,
	getInboundEmail,
	rejectInboundEmail,
	reprocessInboundEmail,
	saveInboundDraft,
} from "@/services/inbound-email.service";
import { DraftTree } from "./DraftTree";
import { EmailPreview } from "./EmailPreview";
import { InboxStatusNotice } from "./InboxStatusNotice";
import { STATUS_LABEL, STATUS_VARIANT, countIncluded, formatEmailDate } from "./status";

type Action = "save" | "approve" | "reject" | "reprocess";

const IN_FLIGHT = ["received", "processing", "approving"];

export function InboxDetail({ id }: { id: string }) {
	const { mutate: mutateAll } = useSWRConfig();
	const { data, error, isLoading, mutate } = useApiSWR(["inbound-email", id], () => getInboundEmail(id), {
		refreshInterval: (latest) => (latest && IN_FLIGHT.includes(latest.email.status) ? 15_000 : 0),
	});
	const email = data?.email;
	const editor = useDraftEditor(email?.draft ?? null, email ? `${email.id}:${email.updated_at}` : "");
	const [busy, setBusy] = useState<Action | null>(null);

	if (error) {
		return (
			<div className="rounded-xl border border-border bg-surface p-6">
				<p className="text-sm text-danger">{error.message || "Failed to load the email."}</p>
				<Button size="sm" variant="outline" className="mt-3" onClick={() => mutate()}>
					Retry
				</Button>
			</div>
		);
	}
	if (isLoading || !data || !email) {
		return (
			<div className="space-y-4" aria-busy="true">
				<Skeleton className="h-24 w-full rounded-xl" />
				<Skeleton className="h-64 w-full rounded-xl" />
			</div>
		);
	}

	async function run(action: Action, fn: () => Promise<string>) {
		setBusy(action);
		try {
			toast.success(await fn());
			await mutate();
			mutateAll((key) => Array.isArray(key) && key[0] === "inbound-emails");
		} catch (err) {
			toast.error((err as Error).message || "Something went wrong.");
		} finally {
			setBusy(null);
		}
	}

	const draft = editor.draft;
	const ready = email.status === "ready";
	const canReprocess = ["failed", "ignored", "rejected", "ready"].includes(email.status) || email.approval_stale;
	const canReject = ["ready", "failed", "received"].includes(email.status) || email.approval_stale;
	const taskCount = draft ? countIncluded(draft.groups) : 0;

	return (
		<div className="space-y-4">
			<header className="rounded-xl border border-border bg-surface p-4">
				<div className="flex flex-wrap items-start justify-between gap-2">
					<h2 className="min-w-0 text-lg font-semibold text-foreground">{email.subject || "(no subject)"}</h2>
					<Badge variant={STATUS_VARIANT[email.status]}>{STATUS_LABEL[email.status]}</Badge>
				</div>
				<p className="mt-0.5 text-xs text-muted">
					From {email.from_name || email.from_email} · received {formatEmailDate(email.created_at)}
					{email.agent_run?.agent_name && ` · drafted by ${email.agent_run.agent_name}`}
				</p>
				<div className="mt-3">
					<InboxStatusNotice email={email} />
				</div>
			</header>

			<EmailPreview email={email} defaultOpen={!draft} />

			{draft && (
				<DraftTree
					draft={draft}
					directory={data.directory}
					editable={ready && !busy}
					onGroupChange={editor.updateGroup}
					onSubtaskChange={editor.updateSubtask}
				/>
			)}

			{(ready || canReprocess || canReject) && (
				<div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-end gap-2 rounded-xl border border-border bg-surface/95 px-4 py-3 shadow-sm backdrop-blur">
					{ready && (
						<p className="mr-auto text-xs text-muted">
							{editor.dirty ? "Unsaved edits." : "Review the draft, then approve."}
						</p>
					)}
					{canReprocess && (
						<Button
							size="sm"
							variant="outline"
							disabled={!!busy}
							onClick={() => run("reprocess", () => reprocessInboundEmail(email.id))}
						>
							{busy === "reprocess" ? "Queuing..." : "Reprocess"}
						</Button>
					)}
					{canReject && (
						<Button
							size="sm"
							variant="destructive_outline"
							disabled={!!busy}
							onClick={() => run("reject", async () => {
								await rejectInboundEmail(email.id);
								return "Email rejected.";
							})}
						>
							{busy === "reject" ? "Rejecting..." : "Reject"}
						</Button>
					)}
					{ready && draft && (
						<>
							<Button
								size="sm"
								variant="outline"
								disabled={!editor.dirty || !!busy}
								onClick={() => run("save", async () => {
									await saveInboundDraft(email.id, draft);
									return "Draft saved.";
								})}
							>
								{busy === "save" ? "Saving..." : "Save draft"}
							</Button>
							<Button
								size="sm"
								disabled={!taskCount || !!busy}
								onClick={() => run("approve", () => approveInboundEmail(email.id, draft))}
							>
								{busy === "approve"
									? "Creating..."
									: `Approve and create ${taskCount} task${taskCount === 1 ? "" : "s"}`}
							</Button>
						</>
					)}
				</div>
			)}
		</div>
	);
}
